import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import DashboardContainer from "../../components/dashboard-container";
import CustomTypography from "../../globalStyles/fonts";
import { networkDesignService } from "../../utils/services/networkDesignService";
import { toast } from "react-toastify";
import { formatDate } from "../../utils/formatDate";

const NetworkDesign = () => {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState("admin"); // Get from auth context
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchDesigns(selectedClient?._id);
  }, [selectedClient]);

  const fetchDesigns = async (client) => {
    setLoading(true);
    try {
      const response = await networkDesignService.fetch("", "", client);
      setDesigns(response.data || []);
    } catch (error) {
      console.error("Error fetching network designs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    navigate("/manage-network-design");
  };

  const handleEdit = (design) => {
    navigate("/manage-network-design", { state: { design } });
  };

  const handleView = (design) => {
    navigate("/view-network-design", { state: { design } });
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm("Are you sure you want to delete this network design?")
    ) {
      return;
    }

    try {
      await networkDesignService.delete(id);
      toast.success("Network design deleted successfully");
      fetchDesigns();
    } catch (error) {
      console.error("Error deleting network design:", error);
      toast.error("Failed to delete network design");
    }
  };

  return (
    <DashboardContainer
      hideUploadButton
      hideGridList
      onSelectClient={(cli) => setSelectedClient(cli)}
      header={"Network Designs"}
      title={"+ Create Design"}
      onClickAddButton={handleCreate}
    >
      <Box sx={{ p: 1 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "40vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Nodes</TableCell>
                  <TableCell>connectors</TableCell>
                  <TableCell>Last Modified</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {designs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box sx={{ py: 4 }}>
                        <CustomTypography variant="body1" color="textSecondary">
                          No network designs found. Create your first design to
                          get started.
                        </CustomTypography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  designs.map((design) => (
                    <TableRow key={design._id}>
                      <TableCell>{design.name}</TableCell>
                      <TableCell>{design.client?.business_name}</TableCell>
                      <TableCell>{design.nodes?.length || 0}</TableCell>
                      <TableCell>{design.edges?.length || 0}</TableCell>
                      <TableCell>
                        {design.metadata?.lastModified
                          ? formatDate(design.metadata.lastModified)
                          : "N/A"}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleView(design)}
                          title="View"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        {userRole === "admin" && (
                          <>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(design)}
                              title="Edit"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(design._id)}
                              title="Delete"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </DashboardContainer>
  );
};

export default NetworkDesign;
